import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PriceDisplay } from './PriceDisplay';
import { ProductImage } from './ProductImage';
import { toast } from './ui/sonner';
import { cartApi, type Product } from '@/lib/api';
import { withClickSpan } from '@/lib/clickSpan';
import { useUiStore } from '@/stores/ui';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const qc = useQueryClient();
  const openCart = useUiStore((s) => s.openCart);

  const addMut = useMutation({
    mutationFn: () => cartApi.add(product.id, 1),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cart'] });
      toast.success(`${product.name} added to cart`);
      openCart();
    },
    onError: (err: Error & { traceId?: string | null }) => {
      toast.error(`Failed to add: ${product.name}`, {
        description: err.traceId ? `Trace: ${err.traceId}` : err.message,
      });
    },
  });

  const outOfStock = product.stock <= 0;

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    void withClickSpan(
      'add-to-cart',
      { 'app.product.id': product.id, 'app.product.sku': product.sku, 'app.product.category': product.category },
      () => addMut.mutateAsync(),
    );
  };

  return (
    <Card className="group overflow-hidden flex flex-col">
      <Link
        to={`/p/${encodeURIComponent(product.id)}`}
        className="block focus-visible:outline-2 focus-visible:outline-(--color-spell-violet)"
        aria-label={`View ${product.name}`}
      >
        <ProductImage
          src={product.image_url || undefined}
          fallbackSrc={`/images/${product.sku.toLowerCase()}.png`}
          alt={product.name}
          category={product.category}
          sku={product.sku}
          className="product-image"
        />
      </Link>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <CardTitle>
            <Link to={`/p/${encodeURIComponent(product.id)}`} className="hover:underline">
              {product.name}
            </Link>
          </CardTitle>
          {outOfStock && <Badge variant="destructive">Out of stock</Badge>}
        </div>
        <Badge variant="secondary" className="w-fit">{product.category}</Badge>
      </CardHeader>
      <CardContent className="mt-auto flex items-center justify-between">
        <PriceDisplay knuts={product.price_knuts} className="text-lg" />
        <Button
          type="button"
          variant="cta"
          size="sm"
          disabled={outOfStock || addMut.isPending}
          onClick={handleAdd}
          aria-label={`Add ${product.name} to cart`}
        >
          {addMut.isPending ? 'Adding…' : 'Add to Cart'}
        </Button>
      </CardContent>
    </Card>
  );
}
